import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import Reveal from '../components/Reveal';
import { PageHero } from './Products';
import { COMPANY, HERO_IMAGES } from '../mock';
import api, { apiErrorMessage } from '../lib/api';

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/submissions', { type: 'contact', ...form });
      toast({ title: 'Message sent!', description: 'A member of our team will get back to you within 24 hours.' });
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast({ title: 'Could not send message', description: apiErrorMessage(err), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const CONTACT = [
    { icon: MapPin, title: 'FCP India', lines: [COMPANY.address] },
    { icon: Phone, title: 'Phone', lines: [...COMPANY.phones, COMPANY.landline] },
    { icon: Mail, title: 'Email', lines: COMPANY.emails },
    { icon: Clock, title: 'Working Hours', lines: ['Mon – Sat: 9:00 AM – 7:00 PM'] },
  ];

  return (
    <div className="bg-[#070a12]">
      <PageHero title="Contact Us" crumb="Contact" image={HERO_IMAGES[0]} />

      <section className="py-28">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <div className="section-eyebrow fire-text">Get In Touch</div>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl mt-4 text-white">Let’s Talk Fire Safety</h2>
            <p className="text-gray-400 text-lg mt-4">Fill out the form and a member of our team will get back to you within 24 hours.</p>
            <div className="mt-10 space-y-4">
              {CONTACT.map((c) => (
                <div key={c.title} className="flex gap-4 glass rounded-2xl p-5">
                  <span className="grid place-items-center w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent text-white shrink-0"><c.icon className="w-5 h-5" /></span>
                  <div><div className="font-heading font-bold text-white">{c.title}</div>{c.lines.map((l) => <p key={l} className="text-gray-400 text-sm break-all">{l}</p>)}</div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={120}>
            <form onSubmit={submit} className="glass rounded-3xl p-8">
              <h3 className="font-heading font-bold text-2xl text-white mb-6">Send Us a Message</h3>
              <div className="space-y-4">
                <Input name="name" value={form.name} onChange={change} required placeholder="Your name" className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
                <Input name="email" type="email" value={form.email} onChange={change} required placeholder="Your email" className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
                <Input name="phone" value={form.phone} onChange={change} placeholder="Phone" className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
                <Textarea name="message" value={form.message} onChange={change} required placeholder="Your message" rows={5} className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 resize-none" />
                <Button type="submit" disabled={submitting} size="lg" className="w-full gap-2 h-12 bg-gradient-to-r from-primary to-accent hover:opacity-90">
                  {submitting ? 'Sending…' : <>Send Message <Send className="w-4 h-4" /></>}
                </Button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
